'use client'

import { ListItemButton, ListItemIcon, ListItemText, Paper } from '@mui/material'
import { type KeyboardEvent, type Ref, useImperativeHandle, useState } from 'react'

import assistantCategoryIcons from '~/features/assistant/mentionIcons'
import useAssistantMentions, { type AssistantMentionCandidate } from '~/features/assistant/useAssistantMentions'

export type MentionPickerHandle = {
  /** Handles the picker's keys (arrows, Enter, Tab, Escape) and reports whether it did. */
  handleKeyDown: (event: KeyboardEvent) => boolean
}

type MentionPickerProps = {
  /** What follows the `@` in the draft. */
  query: string
  onPick: (candidate: AssistantMentionCandidate) => void
  onDismiss: () => void
  ref?: Ref<MentionPickerHandle>
}

/**
 * The entities matching an `@` in the draft, listed above the composer. The input keeps focus: the arrow keys move
 * the highlight, Enter or Tab picks it and Escape closes the list. Mounted only while the draft has an `@`, so the
 * entity lists load on first use rather than with the panel.
 */
export default function MentionPicker({ query, onPick, onDismiss, ref }: MentionPickerProps) {
  const candidates = useAssistantMentions(query)
  const [highlight, setHighlight] = useState({ query, index: 0 })
  const active = highlight.query === query ? Math.min(highlight.index, candidates.length - 1) : 0

  useImperativeHandle(
    ref,
    () => ({
      handleKeyDown: (event) => {
        const candidate = candidates[active]
        if (!candidate) return false
        const move = (step: number) =>
          setHighlight({ query, index: (active + step + candidates.length) % candidates.length })
        switch (event.key) {
          case 'ArrowDown':
            move(1)
            break
          case 'ArrowUp':
            move(-1)
            break
          case 'Enter':
          case 'Tab':
            if (event.shiftKey || event.nativeEvent.isComposing) return false
            onPick(candidate)
            break
          case 'Escape':
            onDismiss()
            break
          default:
            return false
        }
        event.preventDefault()
        return true
      }
    }),
    [active, candidates, onDismiss, onPick, query]
  )

  if (candidates.length === 0) return null
  return (
    <Paper
      elevation={4}
      role="listbox"
      data-testid="assistant-mention-picker"
      sx={{
        position: 'absolute',
        bottom: 'calc(100% + 8px)',
        left: 0,
        right: 0,
        maxHeight: 240,
        overflowY: 'auto',
        p: 0.5,
        borderRadius: 1.5,
        border: 1,
        borderColor: 'divider',
        zIndex: 1
      }}
    >
      {candidates.map((candidate, index) => (
        <ListItemButton
          key={`${candidate.category}-${candidate.id}`}
          role="option"
          aria-selected={index === active}
          selected={index === active}
          dense
          // Keeps focus in the input, so typing carries on after a click.
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => onPick(candidate)}
          sx={{ borderRadius: 1 }}
        >
          <ListItemIcon sx={{ minWidth: 32 }}>{assistantCategoryIcons[candidate.category]}</ListItemIcon>
          <ListItemText
            primary={candidate.label}
            secondary={candidate.subLabel ? `${candidate.category} · ${candidate.subLabel}` : candidate.category}
            slotProps={{
              primary: { noWrap: true, variant: 'body2' },
              secondary: { noWrap: true, variant: 'caption', sx: { textTransform: 'capitalize' } }
            }}
          />
        </ListItemButton>
      ))}
    </Paper>
  )
}
